import { CustomReport } from '@principle-theorem/principle-core';
import {
  DateFilterType,
  ICustomReportData,
  MeasureReducer,
  PatientStatus,
  ReportBuilderDataSourceId,
  ReportFilterComparison,
} from '@principle-theorem/principle-core/interfaces';
import { ALL_REGIONS, Region } from '@principle-theorem/shared';
import { ChartFilterDefinition } from './data-sources/charts/chart-filter-definition';
import { ReportBuilderCharts } from './data-sources/charts/report-builder-charts';
import {
  coldContactReactivationCampaignReportTemplate,
  patientBirthdayCampaignReportTemplate,
  reactivationHealthFundPatientsCampaignReportTemplate,
  reactivationNonHealthFundPatientsCampaignReportTemplate,
} from './marketing-campaign-report-templates';
import { CustomReportFilter } from './models/custom-report-filters';
import { FactTables } from './models/fact-tables';
import { AppointmentEventFactMeasures } from './models/facts/appointment-event-fact';
import { InvoiceEventFactMeasures } from './models/facts/invoice-event-fact';
import { LabJobEventFactMeasures } from './models/facts/lab-job-event-fact';
import { PatientEventFactMeasures } from './models/facts/patient-event-fact';
import { toMeasureBuilderData } from './models/measure-properties';
import { ReportBuilderColumns } from './report-builder-columns';

export enum CustomReportTemplateTag {
  Appointments = 'Appointments',
  Invoices = 'Invoices',
  LabJobs = 'Lab Jobs',
  Patients = 'Patients',
  Template = 'Template',
  MarketingCampaign = 'Marketing Campaign',
}

export interface ICustomReportTemplate extends ICustomReportData {
  name: string;
  description: string;
  dataSourceId: ReportBuilderDataSourceId;
  regions: Region[];
  tags?: string[];
}

export function newPatientsTemplate(
  factTable: AppointmentEventFactMeasures
): ICustomReportTemplate {
  return {
    name: 'New Patient Appointments',
    description: 'Report on new patient appointments',
    regions: ALL_REGIONS,
    tags: [
      CustomReportTemplateTag.Appointments,
      CustomReportTemplateTag.Template,
    ],
    dataSourceId: ReportBuilderDataSourceId.Appointments,
    staticFilters: [
      CustomReportFilter.initDateRange({
        id: factTable.appointment.date.metadata.id,
      }),
      CustomReportFilter.init({
        id: factTable.isFirstAppointment.metadata.id,
        values: [{ value: true, label: 'Yes' }],
      }),
    ],
    chartSections: [
      CustomReport.chartSection('Charts', [
        ReportBuilderCharts.toCustomReportChart(
          factTable.practitioner.name,
          ChartFilterDefinition.numberSummary(
            'Appointments',
            factTable.practitioner.name,
            [
              toMeasureBuilderData(
                factTable.appointment.count,
                MeasureReducer.Sum,
                'Appointments'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.practitioner.name,
          ChartFilterDefinition.row(
            'Practitioner',
            factTable.practitioner.name,
            [
              toMeasureBuilderData(
                factTable.appointment.count,
                MeasureReducer.Sum,
                'Appointments'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.hasNextAppointment,
          ChartFilterDefinition.pie(
            'Has Next Appointment',
            factTable.hasNextAppointment,
            [
              toMeasureBuilderData(
                factTable.appointment.count,
                MeasureReducer.Sum,
                'Appointments'
              ),
            ]
          )
        ),
      ]),
    ],
    columns: ReportBuilderColumns.toColumns([
      factTable.patient.name,
      factTable.appointment.date,
      factTable.practitioner.name,
      factTable.patientLink,
      factTable.hasNextAppointment,
    ]),
  };
}

// function appointmentsByTreatmentTemplate(
//   factTable: AppointmentEventFactMeasures
// ): ICustomReportTemplate {
//   return {
//     name: 'New Patient Appointments by Treatment',
//     description:
//       'Report on new patient appointments using a treatment to identify new patients',
//     tags: [
//       CustomReportTemplateTag.Appointments,
//       CustomReportTemplateTag.Template,
//     ],
//     dataSourceId: ReportBuilderDataSourceId.Appointments,
//     staticFilters: [
//       CustomReportFilter.initDateRange({
//         id: factTable.appointment.date.metadata.id,
//       }),
//       CustomReportFilter.init({
//         id: factTable.treatments.metadata.id,
//         values: [],
//         isPlaceholder: true,
//         placeholderMessage: 'Select a treatment to filter new patients by',
//       }),
//     ],
//     chartSections: [
//       CustomReport.chartSection('Charts', [
//         {
//           ...ReportBuilderCharts.toCustomReportChart(
//             factTable.practitioner.name,
//             ChartFilterDefinition.numberSummary(
//               'New Patients',
//               factTable.practitioner.name,
//               [
//                 toMeasureBuilderData(
//                   factTable.treatments,
//                   MeasureReducer.Sum,
//                   'New Patients',
//                   []
//                 ),
//               ]
//             )
//           ),
//           isPlaceholder: true,
//           placeholderMessage: 'Select a treatment to filter new patients by',
//         },
//       ]),
//     ],
//     columns: ReportBuilderColumns.toColumns([
//       factTable.patient.name,
//       factTable.patient.name,
//       factTable.appointment.date,
//       factTable.practitioner.name,
//       factTable.patientLink,
//       factTable.patient.nextVisitBooked,
//     ]),
//   };
// }

export function rebookingRatesTemplate(
  factTable: AppointmentEventFactMeasures
): ICustomReportTemplate {
  return {
    name: 'Rebooking Rates',
    description: 'Report on rebooking rates for appointments',
    regions: ALL_REGIONS,
    tags: [
      CustomReportTemplateTag.Appointments,
      CustomReportTemplateTag.Template,
    ],
    dataSourceId: ReportBuilderDataSourceId.Appointments,
    staticFilters: [
      CustomReportFilter.initDateRange({
        id: factTable.appointment.date.metadata.id,
      }),
    ],
    chartSections: [
      CustomReport.chartSection('Charts', [
        ReportBuilderCharts.toCustomReportChart(
          factTable.practitioner.name,
          ChartFilterDefinition.numberSummary(
            'Appointments',
            factTable.practitioner.name,
            [
              toMeasureBuilderData(
                factTable.appointment.count,
                MeasureReducer.Sum,
                'Appointments'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.practitioner.name,
          ChartFilterDefinition.row(
            'Practitioner',
            factTable.practitioner.name,
            [
              toMeasureBuilderData(
                factTable.appointment.count,
                MeasureReducer.Sum,
                'Appointments'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.patient.nextVisitBooked,
          ChartFilterDefinition.pie(
            'Next Visit Booked',
            factTable.patient.nextVisitBooked,
            [
              toMeasureBuilderData(
                factTable.appointment.count,
                MeasureReducer.Sum,
                'Appointments'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.nextAppointmentBooked,
          ChartFilterDefinition.pie(
            'Next Appointment Booked at Checkout',
            factTable.nextAppointmentBooked,
            [
              toMeasureBuilderData(
                factTable.appointment.count,
                MeasureReducer.Sum,
                'Appointments'
              ),
            ]
          )
        ),
      ]),
    ],
    columns: ReportBuilderColumns.toColumns([
      factTable.patient.name,
      factTable.appointment.date,
      factTable.practitioner.name,
      factTable.patientLink,
      factTable.patient.nextVisitBooked,
    ]),
  };
}

export function invoiceGstTemplate(
  factTable: InvoiceEventFactMeasures
): ICustomReportTemplate {
  return {
    name: 'Invoice Tax Report',
    description: 'Report on Tax collected from fully paid invoices',
    regions: [Region.Australia, Region.NewZealand],
    tags: [CustomReportTemplateTag.Invoices, CustomReportTemplateTag.Template],
    dataSourceId: ReportBuilderDataSourceId.Invoices,
    staticFilters: [
      CustomReportFilter.initDateRange({
        id: factTable.invoice.createdAt.metadata.id,
      }),
      CustomReportFilter.init({
        id: factTable.invoice.status.metadata.id,
        values: [{ value: 'paid', label: 'Paid' }],
      }),
    ],
    chartSections: [
      CustomReport.chartSection('Charts', [
        ReportBuilderCharts.toCustomReportChart(
          factTable.invoice.createdAt,
          ChartFilterDefinition.numberSummary(
            'Invoices',
            factTable.invoice.createdAt,
            [
              toMeasureBuilderData(
                factTable.invoice.createdAt,
                MeasureReducer.Count,
                'Invoices'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.practice.name,
          ChartFilterDefinition.numberSummary(
            'Total Invoice GST',
            factTable.practice.name,
            [
              toMeasureBuilderData(
                factTable.tax,
                MeasureReducer.Sum,
                'Invoice Tax'
              ),
            ]
          )
        ),
      ]),
    ],
    columns: ReportBuilderColumns.toColumns([
      factTable.patient.name,
      factTable.invoice.createdAt,
      factTable.invoice.total,
      factTable.tax,
      factTable.link,
      factTable.invoice.products,
    ]),
  };
}

export function patientBirthdaysTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return {
    name: 'Patient Birthdays',
    description: 'Report on patient birthdays',
    regions: ALL_REGIONS,
    tags: [CustomReportTemplateTag.Patients, CustomReportTemplateTag.Template],
    dataSourceId: ReportBuilderDataSourceId.Patients,
    staticFilters: [
      CustomReportFilter.init({
        id: factTable.patient.birthday.metadata.id,
        comparison: ReportFilterComparison.DateIs,
        dateType: DateFilterType.ReportDate,
        values: [],
      }),
    ],
    chartSections: [
      CustomReport.chartSection('Charts', [
        ReportBuilderCharts.toCustomReportChart(
          factTable.patient.name,
          ChartFilterDefinition.numberSummary(
            'Patients',
            factTable.patient.name,
            [
              toMeasureBuilderData(
                factTable.patient.count,
                MeasureReducer.Sum,
                'Patients'
              ),
            ]
          )
        ),
      ]),
    ],
    columns: ReportBuilderColumns.toColumns([
      factTable.patient.name,
      factTable.patient.dateOfBirth,
      factTable.patient.age,
      factTable.patient.mobileNumber,
      factTable.patient.email,
      factTable.patientLink,
    ]),
  };
}

export function recallReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return {
    name: 'Patients Due for Recall',
    description: 'Report on patients who are due for recall',
    regions: ALL_REGIONS,
    tags: [CustomReportTemplateTag.Patients, CustomReportTemplateTag.Template],
    dataSourceId: ReportBuilderDataSourceId.Patients,
    staticFilters: [
      CustomReportFilter.init({
        id: factTable.patient.recall.nextRecallDue.dueAt.metadata.id,
        comparison: ReportFilterComparison.DateIsBefore,
        dateType: DateFilterType.ReportDate,
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.nextVisitBooked.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'No', value: false }],
      }),
    ],
    chartSections: [
      CustomReport.chartSection('Charts', [
        ReportBuilderCharts.toCustomReportChart(
          factTable.patient.name,
          ChartFilterDefinition.numberSummary(
            'Patients',
            factTable.patient.name,
            [
              toMeasureBuilderData(
                factTable.patient.count,
                MeasureReducer.Sum,
                'Patients'
              ),
            ]
          )
        ),
      ]),
    ],
    columns: ReportBuilderColumns.toColumns([
      factTable.patient.name,
      factTable.patient.mobileNumber,
      factTable.patient.email,
      factTable.patient.recall.nextRecallDue.dueAt,
      factTable.patient.recall.periodInMonths,
      factTable.patient.recall.lastCompletedAt,
      factTable.patient.nextVisitBooked,
      factTable.patientLink,
    ]),
  };
}

export function labJobsByPractitionerTemplate(
  factTable: LabJobEventFactMeasures
): ICustomReportTemplate {
  return {
    name: 'Lab Jobs by Practitioner',
    description:
      'Report on lab jobs grouped by practitioner with expense visibility',
    regions: ALL_REGIONS,
    tags: [CustomReportTemplateTag.LabJobs, CustomReportTemplateTag.Template],
    dataSourceId: ReportBuilderDataSourceId.LabJobs,
    staticFilters: [
      CustomReportFilter.initDateRange({
        id: factTable.labJob.createdAt.metadata.id,
      }),
    ],
    chartSections: [
      CustomReport.chartSection('Charts', [
        ReportBuilderCharts.toCustomReportChart(
          factTable.practitioner.name,
          ChartFilterDefinition.numberSummary(
            'Lab Jobs',
            factTable.practitioner.name,
            [
              toMeasureBuilderData(
                factTable.count,
                MeasureReducer.Sum,
                'Lab Jobs'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.practitioner.name,
          ChartFilterDefinition.numberSummary(
            'Total Lab Job Cost',
            factTable.practitioner.name,
            [
              toMeasureBuilderData(
                factTable.labJob.cost,
                MeasureReducer.Sum,
                'Total Lab Job Cost'
              ),
            ]
          )
        ),
        ReportBuilderCharts.toCustomReportChart(
          factTable.practitioner.name,
          ChartFilterDefinition.row(
            'Practitioner',
            factTable.practitioner.name,
            [
              toMeasureBuilderData(
                factTable.count,
                MeasureReducer.Sum,
                'Lab Jobs'
              ),
              toMeasureBuilderData(
                factTable.labJob.cost,
                MeasureReducer.Sum,
                'Total Lab Job Cost'
              ),
            ]
          )
        ),
      ]),
    ],
    columns: ReportBuilderColumns.toColumns([
      factTable.practitioner.name,
      factTable.lab.name,
      factTable.labJob.type,
      factTable.labJob.status,
      factTable.labJob.cost,
      factTable.labJob.dueDate,
      factTable.appointment.date,
      factTable.patient.name,
    ]),
  };
}

export function getReportBuilderTemplates(): ICustomReportTemplate[] {
  return [
    newPatientsTemplate(FactTables.appointmentEvent.latestEvent),
    rebookingRatesTemplate(FactTables.appointmentEvent.latestEvent),
    invoiceGstTemplate(FactTables.invoiceEvent.latestEvent),
    labJobsByPractitionerTemplate(FactTables.labJobEvent.latestEvent),
    patientBirthdaysTemplate(FactTables.patientEvent.latestEvent),
    recallReportTemplate(FactTables.patientEvent.latestEvent),
    // TODO: Add more templates
    // Clear Aligner
    // Appointments with Service Code - Chart grouped by status

    // These need to resolve a value from firestore at some point
    // appointmentsByTreatmentTemplate(FactTables.appointmentEvent.latestEvent), // Cant do this yet as I need a treatment value to filter by OR to build it afterwards
    // Appointments by Treatment Category
    patientBirthdayCampaignReportTemplate(FactTables.patientEvent.latestEvent),
    reactivationHealthFundPatientsCampaignReportTemplate(
      FactTables.patientEvent.latestEvent
    ),
    reactivationNonHealthFundPatientsCampaignReportTemplate(
      FactTables.patientEvent.latestEvent
    ),
    coldContactReactivationCampaignReportTemplate(
      FactTables.patientEvent.latestEvent
    ),
  ];
}
